import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Injectable, Injector} from '@angular/core';
import {from, Observable, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {environment} from '@env/environment';
import {AccessDeniedModalService} from '../../../../services/access-denied-modal.service';
import {TokenRefreshService} from './token-refresh.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private tokenRefreshService: TokenRefreshService,
    private injector: Injector,
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.headers.has('SkipAuthorization')) {
      return next.handle(req).pipe(catchError(error => this.handleError(error)));
    }

    // Перед кожним запитом переконуємось, що токен ще живий (після сну / фонової вкладки
    // таймер оновлення міг не спрацювати вчасно). keycloak-js об'єднує паралельні оновлення в одне.
    return from(this.tokenRefreshService.ensureFreshToken()).pipe(
      switchMap(() => next.handle(this.authorize(req))),
      catchError((error: HttpErrorResponse) => {
        if (this.shouldRetryAfterRefresh(error, req)) {
          // 401 — пробуємо один раз примусово оновити токен і повторити запит.
          // Ніякого logout: якщо сесію справді відхилено, keycloak-js сам перекине на login().
          return from(this.tokenRefreshService.forceRefresh()).pipe(
            switchMap(refreshed => refreshed ? next.handle(this.authorize(req)) : throwError(error)),
          );
        }

        return throwError(error);
      }),
      catchError(error => this.handleError(error)),
    );
  }

  private authorize(req: HttpRequest<any>): HttpRequest<any> {
    const token = this.tokenRefreshService.getToken();

    if (!token) {
      return req;
    }

    return req.clone({
      withCredentials: false,
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private shouldRetryAfterRefresh(error: HttpErrorResponse, req: HttpRequest<any>): boolean {
    return error instanceof HttpErrorResponse
      && error.status === 401
      && !req.url.includes(environment.workerRoot);
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (this.isAccessDenied(error)) {
      const accessDeniedService = this.injector.get(AccessDeniedModalService);
      accessDeniedService.showAccessDeniedModal();
    }

    return throwError(error);
  }

  private isAccessDenied(error: HttpErrorResponse): boolean {
    const response = (error && error.error) || {};
    return error.status === 403 ||
      (error.status === 400 && (Number(response.status) === 403 || response.title === '403 Forbidden'));
  }
}
